import type { Feature, FeatureCollection, Polygon, MultiPolygon } from "geojson";
import { z } from "zod";

export type Coordinates = [number, number];
export type LatLng = [number, number];

export type Locale = "en" | "th" | "zh";

export type FallbackTier =
  | "live"
  | "database"
  | "cache"
  | "scenario"
  | "reference"
  | "unavailable";

export interface SourceMeta {
  source: string;
  fetchedAt: string;
  ageMinutes: number;
  fallbackTier: FallbackTier;
  /**
   * When the observation was made, when the feed knows it.
   *
   * `fetchedAt` says when we asked; this says when the data was measured. A
   * daily report fetched at 09:00 was measured at 00:00 — without this, the
   * age is computed from the fetch and a fourteen-hour-old observation is
   * reported as freshly live.
   */
  observedAt?: string;
  /**
   * Human-readable reason set when `fallbackTier` is "unavailable" or "scenario" —
   * surfaced by `/api/health/detailed` and shown inline in the SOURCES catalog so
   * operators can distinguish missing API keys from upstream outages.
   * Example: "Missing GEMINI_API_KEY env var — adapter disabled".
   */
  note?: string;
  /**
   * True when the value shown is the last successful fetch because a refresh
   * failed. Health accounting keys off this flag rather than matching prose
   * in `note`, so rewording the message can never silently flip a feed from
   * "degraded" to "success".
   */
  staleRefresh?: boolean;
}

export interface NormalizedFeed<T> {
  features: T[];
  meta: SourceMeta;
}

// ---- Incidents ----

export type IncidentCategory =
  | "traffic-accident"
  | "traffic-congestion"
  | "construction"
  | "flooding"
  | "waste"
  | "lighting"
  | "sidewalk"
  | "drainage"
  | "trees"
  | "other";

export type IncidentSeverity = "low" | "medium" | "high" | "critical";

export type IncidentStatus =
  | "received"
  | "assigned"
  | "in-progress"
  | "resolved";

export interface IncidentFeature {
  id: string;
  ticketNumber?: string;
  lat: number;
  lng: number;
  category: IncidentCategory;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  description?: string;
  reportedAt: string;
  resolvedAt?: string;
  imageUrl?: string;
  /** Public URL of the original report, so an operator can click an incident
   *  and jump to where it was reported (e.g. the Traffy Fondue ticket page) to
   *  go fix it. Omitted when the source exposes no public permalink. */
  sourceUrl?: string;
  reporterPlatform: "traffy" | "city-reporter" | "itic" | "internal";
  zoneId?: string;
  raw?: Record<string, unknown>;
}

// ---- Traffic ----

export interface TrafficSample {
  lat: number;
  lng: number;
  intensity: number;
  roadClass: "arterial" | "collector" | "local" | "campus-internal";
}

export interface TrafficCorridor {
  id: string;
  name: string;
  segments: Coordinates[];
  roadClass: TrafficSample["roadClass"];
}

// ---- Environment ----

export interface AirQualityPoint {
  lat: number;
  lng: number;
  station: string;
  aqi: number | null;
  aqiScale?: "thai-pcd" | "us-epa";
  pm25: number | null;
  category: "good" | "moderate" | "unhealthy-sg" | "unhealthy" | "very-unhealthy" | "hazardous" | null;
  observedAt: string;
  source: string;
}

export interface WeatherSnapshot {
  tempC: number | null;
  feelsLikeC: number | null;
  humidity: number | null;
  windKmh: number | null;
  precipMm: number | null;
  condition: string;
  observedAt: string;
}

export interface PrecipNowcast {
  /** Start of the first model interval, with explicit timezone. */
  validAt?: string;
  /** Modelled precipitation amount in the first 15-minute forecast interval; not mm/h or a gauge measurement. */
  nowMm: number;
  /** Cumulative mm forecast across the next 2 hours. */
  total2hMm: number;
  /** Max single-interval mm in the next 2 hours. */
  peakMm: number;
  /** ISO timestamp of the peak interval, or null if dry. */
  peakAt: string | null;
  /** ISO timestamp of the first interval with ≥ 0.5 mm, or null if none. */
  firstSignificantAt: string | null;
  /** Minutes from now to the first significant interval (multiple of 15), or null. */
  minutesToSignificant: number | null;
  /** Coarse bucket for UI colouring. */
  intensity: "dry" | "light" | "moderate" | "heavy";
  /** Per-15-min forecast points covering the next ~2 hours. */
  points: Array<{ at: string; mm: number; prob: number }>;
}

export interface AcademicPhase {
  /** ISO date when this phase starts. */
  start: string;
  /** ISO date when this phase ends. */
  end: string;
  /** Stable id — used by the UI for icon/colour mapping. */
  id:
    | "semester-1"
    | "semester-1-finals"
    | "semester-2"
    | "semester-2-finals"
    | "summer-term"
    | "break"
    | "freshy-week"
    | "graduation"
    | "holiday";
  /** Short human label, EN. */
  label: string;
  /** One-sentence description of operational impact ("expect higher campus traffic" etc.). */
  describe: string;
}

export interface AcademicSnapshot {
  current: AcademicPhase | null;
  next: AcademicPhase | null;
  /** Days from today (UTC) to `next.start`. Negative if `current` is the next event itself. */
  daysToNext: number | null;
  /** Convenience: campus operational tempo derived from current phase. */
  tempo: "low" | "normal" | "high" | "peak";
}

// ---- News / Vibes ----

export type IntelligenceKind =
  | "news"
  | "social"
  | "incident"
  | "weather"
  | "movement";

export interface IntelligenceItem {
  id: string;
  title: string;
  summary: string;
  source: string;
  sourceUrl: string;
  publishedAt: string;
  tags: string[];
  score: number;
  kind: IntelligenceKind;
  imageUrl?: string;
  /** Geocoded latitude when a known place is mentioned in the story */
  lat: number | null;
  /** Geocoded longitude when a known place is mentioned in the story */
  lng: number | null;
  /** Name of the matched place (e.g. "Talat Chonburi") */
  placeName: string | null;
}

// ---- Campus GeoJSON ----

export interface CampusZoneProperties {
  zoneId: string;
  name: { en: string; th: string; zh: string };
  zoneType:
    | "academic"
    | "residential"
    | "athletic"
    | "park"
    | "commercial"
    | "service"
    | "perimeter";
  color?: string;
  height?: number | null;
}

export type CampusZoneFeature = Feature<Polygon | MultiPolygon, CampusZoneProperties>;
export type CampusZoneCollection = FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>;

// ---- Executive / Strategic ----

export interface RankingEntry {
  system: "qs-world" | "qs-asia" | "the-world" | "the-asia";
  label: string;
  rank: number;
  total: number;
  year: number;
  previousRank: number;
  trend: "up" | "down" | "stable";
}

export interface EnrollmentSnapshot {
  total: number;
  undergraduate: number;
  graduate: number;
  international: number;
  internationalPct: number;
  faculties: number;
  studentFacultyRatio: string;
}

export interface ResearchSnapshot {
  publications2024: number;
  citations2024: number;
  hIndex: number;
  topFields: string[];
  researchFundingMThb: number | null;
  patentsFiled: number;
}

export interface FinancialSnapshot {
  annualBudgetBThb: number | null;
  researchGrantsMThb: number | null;
  endowmentBThb: number | null;
  note: string;
}

export interface StrategicInitiative {
  id: string;
  name: string;
  status: "on-track" | "at-risk" | "delayed" | "completed";
  progressPct: number;
  owner: string;
  deadline: string;
  describe: string;
}

export interface PeerSnapshot {
  name: string;
  country: string;
  qsWorldRank: number;
  theWorldRank: number;
  studentsTotal: number;
  internationalPct: number;
  researchOutput: string;
}

export interface StrategicAlert {
  id: string;
  level: "info" | "watch" | "warning" | "critical";
  category: "environment" | "safety" | "reputation" | "operations" | "finance";
  title: string;
  message: string;
  issuedAt: string;
  source: string;
  actionRequired?: string;
}

export interface ExecutiveSnapshot {
  rankings: RankingEntry[];
  enrollment: EnrollmentSnapshot;
  research: ResearchSnapshot;
  finance: FinancialSnapshot;
  initiatives: StrategicInitiative[];
  peers: PeerSnapshot[];
  alerts: StrategicAlert[];
  updatedAt: string;
}

// ── Global markets — FMP (live ticks) + FRED (macro series) ─────────
export interface MarketTick {
  symbol: string;
  name: string;
  group: "index" | "forex" | "commodity" | "macro";
  value: number | null;
  changePct: number | null;
  asOf: string;
}

export interface MarketSnapshot {
  ticks: MarketTick[];
  thb: { vs: "USD" | "EUR" | "JPY" | "CNY"; rate: number | null }[];
}

// ── Digital Twin ────────────────────────────────────────────────────

export type TwinKind =
  | "building"
  | "sensor"
  | "road"
  | "reservoir"
  | "vessel"
  | "zone"
  | "poi"
  | "bridge"
  | "ferry"
  | "port";

export const TwinKindSchema = z.enum([
  "building",
  "sensor",
  "road",
  "reservoir",
  "vessel",
  "zone",
  "poi",
  "bridge",
  "ferry",
  "port",
]);

export type TwinRelationPredicate =
  | "contains"
  | "monitors"
  | "adjacent_to"
  | "connected_to"
  | "serves"
  | "located_in"
  | "part_of";

export interface TwinObject {
  id: string;
  kind: TwinKind;
  name: string;
  nameTh?: string;
  nameEn?: string;
  lat: number;
  lng: number;
  geom?: unknown;
  properties: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export const TwinObjectSchema = z.object({
  id: z.string(),
  kind: TwinKindSchema,
  name: z.string(),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  lat: z.number(),
  lng: z.number(),
  geom: z.unknown().optional(),
  properties: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export interface TwinRelation {
  id: string;
  subjectId: string;
  predicate: TwinRelationPredicate;
  objectId: string;
  properties?: Record<string, unknown>;
  createdAt: string;
}

export interface TwinStatePoint {
  time: string;
  objectId: string;
  metric: string;
  value: number;
  source: string;
  properties?: Record<string, unknown>;
}

export type AdapterStatus = "healthy" | "degraded" | "down" | "unknown";

export interface AdapterHealth {
  name: string;
  status: AdapterStatus;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  consecutiveFailures: number;
  totalCalls: number;
  totalErrors: number;
  ageMinutes: number | null;
}

// ── GISTDA (Thailand Geo-Informatics & Space Technology) ────────────

export interface GistdaPoi {
  id: number;
  name: string;
  nameEn: string;
  category:
    | "government"
    | "school"
    | "temple"
    | "hospital"
    | "hotel"
    | "bank"
    | "restaurant"
    | "shopping"
    | "transport"
    | "sport"
    | "agency"
    | "other";
  subcat: number;
  road: string;
  roadEn: string;
  lat: number;
  lng: number;
  disabled: string;
}

export interface GistdaSolarBuilding {
  id: number;
  height: number;
  area: number;
  solarIrr: number; // kWh/m² monthly
  roofType: string;
  buildType: string;
  month: string;
  monthNum: number;
  lat: number;
  lng: number;
}

export interface GistdaLandUse {
  id: number;
  code: string;
  name: string;
  nameEn: string;
  area: number;
  lat: number;
  lng: number;
}

/**
 * Satellite-derived climate readings at Chonburi centroid.
 * Source: NASA POWER API (MERRA-2 reanalysis + satellite obs), ~3-day latency.
 * No API key required.
 */
export interface NasaEarthReadings {
  tempC: number | null;
  precipMmDay: number | null;
  solarMJm2: number | null;
  solarKWhm2: number | null;    // solarMJm2 / 3.6
  clearnessIndex: number | null; // ALLSKY_KT 0–1
  dataDate: string;              // YYYYMMDD
  source: "nasa-power-merra2";
}

// ── Isochrone (Geoapify) ─────────────────────────────────────────────────────

export type IsochroneMode = "walk" | "bicycle" | "drive" | "approximated_transit";

export interface IsochroneResult {
  lng: number;
  lat: number;
  minutes: number;
  mode: IsochroneMode;
  geometry: {
    type: "Polygon" | "MultiPolygon";
    coordinates: number[][][] | number[][][][];
  };
  areaKm2: number | null;
}

// ── CV / CCTV detection events ───────────────────────────────────────────────

export interface CvBoundingBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CvDetection {
  class: string;
  confidence: number;
  bbox?: CvBoundingBox;
}

export interface CvDetectionEvent {
  cameraId: string;
  timestamp: string;
  detections: CvDetection[];
  snapshotUrl?: string;
  model?: string;
  counts: Record<string, number>;
}

/** Facebook page post — mirrors the API adapter's FacebookPost interface. */
export interface FacebookPost {
  id: string;
  message: string;
  permalink: string;
  createdAt: string;
  image?: string;
  reactions?: number;
  comments?: number;
  shares?: number;
}

/** Public source discovery; publication dates are distinct from retrieval time. */
export interface PublicDataset {
  id: string;
  title: string;
  description: string;
  organization: string;
  license: string;
  sourceUrl: string;
  updatedAt: string | null;
  resources: { id: string; name: string; format: string; url: string; updatedAt: string | null }[];
}

export interface WaterGauge {
  confidence?: string;
  relayUrl?: string;
  id: string;
  name: string;
  lat: number;
  lng: number;
  province: string;
  river: string;
  levelMsl: number | null;
  bankMsl: number | null;
  situationLevel: number | null;
  observedAt: string | null;
  ageMinutes: number | null;
  current: boolean;
  sourceUrl: string;
}

export interface ReservoirObservation {
  id: string;
  name: string;
  /** Sub-district / district, as the province publishes it. */
  district: string | null;
  /** % of conservation capacity — not of gross capacity. */
  capacityPct: number | null;
  currentVolMCM: number | null;
  maxVolMCM: number | null;
  /**
   * Usable water above dead storage ÷ today's net depletion (outflow − inflow),
   * in days. Null when the dam is filling or in balance, or when the flows
   * are not published — it is never a guess.
   */
  daysRemaining: number | null;
  inflowMCM: number | null;
  outflowMCM: number | null;
  /** "rising" = filling, "falling" = draining, "stable" = balanced, "unknown" = flows not published. */
  trend: "rising" | "falling" | "stable" | "unknown";
  /** The RID report date this row is built on. */
  observedAt: string;
  sourceUrl: string;
}


export interface RainGauge {
  id: string; name: string; lat: number; lng: number;
  rain24hMm: number | null; rain1hMm: number | null;
  observedAt: string | null; ageMinutes: number | null; current: boolean; confidence: string;
  sourceUrl: string; relayUrl: string;
}

// ---- Mayor's Desk ----

/** The language a desk story was published in. */
export type DeskLang = "th" | "en" | "ja" | "zh" | "ko" | "de" | "fr" | "other";

/**
 * How the headline the mayor reads was produced.
 *
 *  - `source`   — the mayor is reading the publisher's own words: the story
 *    was already in Thai, or it is in English and no Thai rendering was
 *    produced (English is a language the mayor reads).
 *  - `machine`  — the mayor is reading a machine translation into Thai. The
 *    publisher's original stays beside it so it can be checked.
 *  - `unavailable` — neither: the story is in a language the mayor cannot
 *    read and no Thai rendering could be produced. It is not put on the desk,
 *    and the gap is counted and reported rather than padded away.
 */
export type DeskTranslation = "source" | "machine" | "unavailable";

export interface DeskStory {
  id: string;
  /** The headline exactly as the publisher wrote it. Always present. */
  title: string;
  /** The language of `title`. */
  lang: DeskLang;
  /**
   * Thai rendering — the headline the mayor reads. The original when the
   * story is in Thai, a machine translation when it is not, and **empty** when
   * no Thai rendering could be produced, so nothing is ever passed off as a
   * translation that is not one.
   */
  titleTh: string;
  /**
   * English rendering. The original when the story is in English; empty
   * otherwise.
   *
   * The desk is read in Thai, so a Thai headline is what earns a story its
   * place. The publisher's original always stays in `title` beside it, which
   * is how a foreign headline can be checked against the machine translation
   * and how a mistranslated number can be caught.
   */
  titleEn: string;
  translation: DeskTranslation;
  /** The publisher, as named by the feed. */
  source: string;
  sourceUrl: string;
  publishedAt: string;
  /** Relevance to Chonburi Town Municipality, 0-100. */
  score: number;
  /** Action tags inherited from the news adapter (EM / PU / FU / PO …). */
  tags: string[];
  /** A known place inside the municipality, when the story names one. */
  place: string | null;
  /**
   * Why the translation failed, when it did.
   *
   * A quota and a timeout want different fixes, so the reason is kept
   * rather than reduced to "it did not work".
   */
  note?: string;
}

/**
 * The Mayor's Desk feed — the news half of the desk.
 *
 * The gaps are part of the contract. A language whose feed answered but
 * found nothing about the municipality is reported as empty, and a story that
 * could not be rendered in Thai is counted as unreadable — neither is padded
 * away, because an empty desk that looks full is the one lie this surface
 * cannot tell.
 */
export interface DeskFeed extends NormalizedFeed<DeskStory> {
  /** The languages the desk drew from, in feed order. */
  languages: DeskLang[];
  /** A language whose feed answered but produced nothing about the municipality. */
  emptyLanguages: DeskLang[];
  /**
   * A language whose feed did not answer at all.
   *
   * Different from an empty language, and never reported as one: a feed
   * that timed out is a broken source, and a desk that says "no coverage
   * about the municipality" when the source was simply down is telling the
   * mayor there is no news when the truth is that nobody looked.
   */
  failedLanguages: DeskLang[];
  /** A language whose stories exist but could not be rendered in Thai. */
  untranslatedLanguages: DeskLang[];
  /** Stories that exist but could not be rendered in Thai, and so are not on the desk. */
  unreadable: number;
  /** True when at least one headline on the desk was machine-translated. */
  translated: boolean;
  /**
   * The stories that could not be rendered in Thai, sent anyway.
   *
   * The API's own address is throttled by the public translation services —
   * a Worker's egress is shared with every other tenant on it — while a
   * reader's browser is not. So the stories are handed to the browser, which
   * translates what it can and leaves the rest to be counted as gaps. The
   * alternative is a desk whose foreign compartment is empty every morning
   * for reasons that have nothing to do with Chonburi.
   */
  untranslated: DeskStory[];
  /**
   * How many headlines each language has that are not in Thai yet.
   *
   * The split is what lets the desk subtract exactly what the browser
   * managed, so the gap it reports afterwards is a count, not a shrug.
   */
  untranslatedByLanguage: Record<string, number>;
}

